/**
 * Wishlist functionality for Shopify theme
 * Handles add/remove wishlist items, count updates, and state management
 */

const WISHLIST_API_BASE = '/apps/ayudra/api';
const WISHLIST_STORAGE_KEY = 'wishlist-products';

class WishlistManager {
  constructor() {
    this.wishlistProducts = new Set();
    this.pendingRequests = new Map();
    this.customerId = null;
    this.isLoggedIn = false;
    this.init();
  }

  init() {
    this.checkLoginStatus();
    this.loadWishlistFromStorage();
    this.setupEventListeners();
    this.setupCountObserver();

    // Delay initialization to ensure DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => {
          // Re-check login status before initializing to ensure customer ID is available
          this.checkLoginStatus();
          this.initializeWishlistState();
        }, 200);
      });
    } else {
      setTimeout(() => {
        // Re-check login status before initializing to ensure customer ID is available
        this.checkLoginStatus();
        this.initializeWishlistState();
      }, 200);
    }
  }

  setupCountObserver() {
    // Watch for when the count element appears in the DOM
    const observer = new MutationObserver((mutations) => {
      const countElement = document.querySelector('[data-wishlist-count]');
      if (countElement && this.customerId) {
        this.loadWishlistCount();
        // Stop observing once we've found it and loaded the count
        observer.disconnect();
      }
    });

    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Also check immediately in case element already exists
    setTimeout(() => {
      const countElement = document.querySelector('[data-wishlist-count]');
      if (countElement && this.customerId) {
        this.loadWishlistCount();
        observer.disconnect();
      }
    }, 100);
  }

  checkLoginStatus() {
    // Check if customer is logged in via Liquid variable or window.Shopify
    const customerElement = document.querySelector('[data-customer-id]');
    if (customerElement && customerElement instanceof HTMLElement) {
      this.customerId = customerElement.dataset.customerId;
      this.isLoggedIn = !!this.customerId && this.customerId !== 'null' && this.customerId !== '';
    } else {
      // Fallback: check window.Shopify.customer if available
      const shopifyAny = /** @type {any} */ (window.Shopify);
      const shopifyCustomer = shopifyAny?.customer;
      this.isLoggedIn = !!(shopifyCustomer?.id);
      this.customerId = shopifyCustomer?.id || null;
    }
  }

  loadWishlistFromStorage() {
    try {
      const stored = sessionStorage.getItem(WISHLIST_STORAGE_KEY);
      if (stored) {
        const products = JSON.parse(stored);
        this.wishlistProducts = new Set(products);
      }
    } catch (e) {
      console.error('Error loading wishlist from storage:', e);
    }
  }

  saveWishlistToStorage() {
    try {
      sessionStorage.setItem(
        WISHLIST_STORAGE_KEY,
        JSON.stringify(Array.from(this.wishlistProducts))
      );
    } catch (e) {
      console.error('Error saving wishlist to storage:', e);
    }
  }

  setupEventListeners() {
    // Event delegation for wishlist heart buttons
    document.addEventListener('click', (e) => {
      if (!(e.target instanceof Element)) return;

      const heartButton = e.target.closest('[data-wishlist-heart]');
      if (!heartButton) return;

      e.preventDefault();
      e.stopPropagation();

      const productId = heartButton instanceof HTMLElement ? heartButton.dataset.productId : null;
      if (!productId) {
        console.warn('Wishlist: Product ID not found on button');
        return;
      }

      if (heartButton instanceof HTMLElement) {
        this.toggleWishlist(productId, heartButton);
      }
    }, true); // Use capture phase to catch early

    // Listen for login state changes
    window.addEventListener('storage', (e) => {
      if (e.key === 'customer-id') {
        this.checkLoginStatus();
        this.loadWishlistCount();
      }
    });

    // Listen for page navigation (Shopify theme navigation)
    window.addEventListener('page:loaded', () => {
      setTimeout(() => {
        this.checkLoginStatus();
        this.loadWishlistCount();
      }, 100);
    });

    // Listen for Shopify view transition pagereveal event
    window.addEventListener('pagereveal', () => {
      setTimeout(() => {
        this.checkLoginStatus();
        this.loadWishlistCount();
      }, 200);
    });

    // Also listen for popstate (browser back/forward)
    window.addEventListener('popstate', () => {
      setTimeout(() => {
        this.checkLoginStatus();
        this.loadWishlistCount();
      }, 100);
    });

    // Listen for cart update events to preserve wishlist count bubble
    document.addEventListener('cart:update', () => {
      // Store current count before potential DOM changes
      const currentCountElement = document.querySelector('[data-wishlist-count]');
      const currentCount = currentCountElement ? parseInt(currentCountElement.textContent || '0', 10) : null;

      // Reload count after a delay to ensure DOM is stable
      setTimeout(() => {
        if (currentCount !== null && currentCount > 0) {
          // Restore count immediately if we had a valid count
          this.updateHeaderCount(currentCount);
        }
        // Then reload from API to ensure accuracy
        this.checkLoginStatus();
        this.loadWishlistCount();
      }, 300);
    });

    // Also listen for cart:updated event
    document.addEventListener('cart:updated', () => {
      setTimeout(() => {
        this.checkLoginStatus();
        this.loadWishlistCount();
      }, 300);
    });

    // Sync states when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.syncHeartIconStates();
      });
    } else {
      // DOM already loaded
      setTimeout(() => this.syncHeartIconStates(), 100);
    }
  }

  // Extract numeric product ID from GID format
  /**
   * @param {string|number|undefined} productId
   * @returns {string|null}
   */
  extractProductId(productId) {
    if (!productId) return null;
    if (typeof productId === 'string' && productId.startsWith('gid://shopify/Product/')) {
      return productId.replace('gid://shopify/Product/', '');
    }
    return String(productId);
  }


  /**
   * @param {string|number} productId
   * @param {HTMLElement} buttonElement
   */
  async toggleWishlist(productId, buttonElement) {

    if (!this.isLoggedIn) {
      this.showLoginAlert();
      return;
    }

    if (!this.customerId) {
      console.error('Wishlist: Customer ID not available');
      this.checkLoginStatus(); // Re-check login status
      if (!this.customerId) {
        this.showLoginAlert();
        return;
      }
    }

    // Extract numeric ID
    const numericId = this.extractProductId(productId);
    if (!numericId) {
      console.error('Invalid product ID:', productId);
      return;
    }

    // Prevent duplicate requests
    if (this.pendingRequests.has(numericId)) {
      return;
    }

    const isWishlisted = this.wishlistProducts.has(numericId);
    const requestKey = `${numericId}-${Date.now()}`;
    this.pendingRequests.set(numericId, requestKey);

    try {
      if (isWishlisted) {
        await this.removeFromWishlist(numericId);
      } else {
        await this.addToWishlist(numericId);
      }
    } catch (error) {
      console.error('Wishlist toggle error:', error);
      this.revertButtonState(buttonElement, isWishlisted);
    } finally {
      this.pendingRequests.delete(numericId);
    }
  }

  /**
   * @param {string|number} productId
   */
  async addToWishlist(productId) {
    if (!this.customerId) {
      console.error('Wishlist: Cannot add - customer ID missing');
      return;
    }


    // Optimistically update UI
    this.updateWishlistState(productId, true);

    try {
      const response = await fetch(`${WISHLIST_API_BASE}/wishlist/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerId: this.customerId,
          productId: productId,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        // Handle "already added" gracefully
        if (data.message?.includes('already added')) {
          // Product is already in wishlist, ensure state is correct
          this.updateWishlistState(productId, true);
        } else {
          throw new Error(data.message || 'Failed to add to wishlist');
        }
      }

      this.wishlistProducts.add(productId);
      this.saveWishlistToStorage();
      await this.updateWishlistCount();
    } catch (error) {
      // Revert optimistic update on error
      this.updateWishlistState(productId, false);
      throw error;
    }
  }

  /**
   * @param {string|number} productId
   */
  async removeFromWishlist(productId) {
    if (!this.customerId) {
      console.error('Wishlist: Cannot remove - customer ID missing');
      return;
    }


    // Optimistically update UI
    this.updateWishlistState(productId, false);

    try {
      const response = await fetch(`${WISHLIST_API_BASE}/wishlist/remove`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerId: this.customerId,
          productId: productId,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to remove from wishlist');
      }

      this.wishlistProducts.delete(productId);
      this.saveWishlistToStorage();
      await this.updateWishlistCount();

      // Remove from wishlist page if present
      this.removeFromWishlistPage(productId);
    } catch (error) {
      // Revert optimistic update on error
      this.updateWishlistState(productId, true);
      throw error;
    }
  }

  /**
   * @param {string|number} productId
   * @param {boolean} isWishlisted
   */
  updateWishlistState(productId, isWishlisted) {
    // Extract numeric ID for comparison
    const numericId = this.extractProductId(productId);
    if (!numericId) return;

    // Update all heart icons for this product (handle both GID and numeric formats)
    const heartButtons = document.querySelectorAll('[data-wishlist-heart]');

    heartButtons.forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      const buttonProductId = button.dataset.productId ? this.extractProductId(button.dataset.productId) : null;
      if (buttonProductId && buttonProductId === numericId) {
        if (isWishlisted) {
          button.classList.add('wishlist-heart--active');
          button.setAttribute('aria-label', 'Remove from wishlist');
        } else {
          button.classList.remove('wishlist-heart--active');
          button.setAttribute('aria-label', 'Add to wishlist');
        }
      }
    });
  }

  /**
   * @param {HTMLElement} buttonElement
   * @param {boolean} previousState
   */
  revertButtonState(buttonElement, previousState) {
    if (previousState) {
      buttonElement.classList.add('wishlist-heart--active');
    } else {
      buttonElement.classList.remove('wishlist-heart--active');
    }
  }

  /**
   * @param {string|number} productId
   */
  removeFromWishlistPage(productId) {
    const numericId = this.extractProductId(productId);
    if (!numericId) return;

    // Try both GID and numeric formats
    let productCard = document.querySelector(`[data-wishlist-product-id="${numericId}"]`);
    if (!productCard) {
      productCard = document.querySelector(`[data-wishlist-product-id="${productId}"]`);
    }

    if (productCard && productCard instanceof HTMLElement) {
      // Find the parent <li> element (product-grid__item) to remove the entire grid item
      const listItem = productCard.closest('.product-grid__item');
      const elementToRemove = listItem || productCard;

      // Apply transition to the element being removed
      if (elementToRemove instanceof HTMLElement) {
        elementToRemove.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
        elementToRemove.style.opacity = '0';
        elementToRemove.style.transform = 'scale(0.9)';
      }

      setTimeout(() => {
        elementToRemove.remove();
        this.checkEmptyWishlist();
      }, 300);
    }
  }

  checkEmptyWishlist() {
    const wishlistContainer = document.querySelector('[data-wishlist-products]');
    if (!wishlistContainer) return;

    const productCards = wishlistContainer.querySelectorAll(
      '[data-wishlist-product-id]'
    );

    if (productCards.length === 0) {
      const emptyMessage = document.querySelector('[data-wishlist-empty]');
      if (emptyMessage && emptyMessage instanceof HTMLElement) {
        emptyMessage.style.display = 'block';
      }
    }
  }

  async loadWishlistCount() {
    // Re-check login status before loading count
    this.checkLoginStatus();

    if (!this.isLoggedIn || !this.customerId) {
      this.updateHeaderCount(0);
      return;
    }

    try {
      const response = await fetch(
        `${WISHLIST_API_BASE}/wishlist/counts/${this.customerId}`
      );
      const data = await response.json();


      if (data.success && data.data) {
        const count = data.data.totalWishlistsProducts || 0;
        this.updateHeaderCount(count);
      } else {
        console.warn('Wishlist: Count API returned unsuccessful response:', data);
        this.updateHeaderCount(0);
      }
    } catch (error) {
      console.error('Error loading wishlist count:', error);
      this.updateHeaderCount(0);
    }
  }

  async updateWishlistCount() {
    await this.loadWishlistCount();
  }

  /**
   * @param {number} count
   */
  updateHeaderCount(count) {
    const countElement = document.querySelector('[data-wishlist-count]');
    if (countElement) {
      countElement.textContent = count < 100 ? String(count) : '';
      countElement.classList.toggle('hidden', count === 0);

      const bubble = countElement.closest('.wishlist-bubble');
      if (bubble) {
        bubble.classList.toggle('visually-hidden', count === 0);
        bubble.classList.toggle('wishlist-bubble--has-items', count > 0);
      }
    } else {
      console.warn('Wishlist: Count element not found, will retry');
      // Retry after a short delay if element not found
      setTimeout(() => {
        const retryElement = document.querySelector('[data-wishlist-count]');
        if (retryElement) {
          retryElement.textContent = count < 100 ? String(count) : '';
          retryElement.classList.toggle('hidden', count === 0);
          const retryBubble = retryElement.closest('.wishlist-bubble');
          if (retryBubble) {
            retryBubble.classList.toggle('visually-hidden', count === 0);
            retryBubble.classList.toggle('wishlist-bubble--has-items', count > 0);
          }
        }
      }, 500);
    }
  }

  /**
   * Opens the theme account UI (desktop popover or mobile drawer) so guests can log in.
   * Falls back to the login URL if those elements are not in the DOM.
   */
  showLoginAlert() {
    const isDesktop = window.matchMedia('(min-width: 750px)').matches;

    const tryOpenAccountPopover = () => {
      const panel = document.querySelector('.account-popover__panel#account-popover');
      if (!(panel instanceof HTMLElement) || typeof panel.showPopover !== 'function') return false;
      try {
        panel.showPopover();
        return true;
      } catch (e) {
        console.warn('Wishlist: Could not open account popover', e);
        return false;
      }
    };

    if (isDesktop && tryOpenAccountPopover()) return;

    const accountDrawer = /** @type {HTMLElement & { showDialog?: () => void } | null} */ (
      document.querySelector('dialog-component.account-drawer')
    );
    if (accountDrawer && typeof accountDrawer.showDialog === 'function') {
      accountDrawer.showDialog();
      return;
    }

    if (!isDesktop && tryOpenAccountPopover()) return;

    window.location.href = '/account/login';
  }

  /**
   * @param {string|number} productId
   * @returns {boolean}
   */
  isProductWishlisted(productId) {
    return this.wishlistProducts.has(productId);
  }

  /**
   * @param {string|number} customerId
   * @param {number} [page]
   * @param {number} [limit]
   * @returns {Promise<any>}
   */
  async loadWishlistProducts(customerId, page = 1, limit = 12) {
    try {
      const response = await fetch(
        `${WISHLIST_API_BASE}/wishlist/${customerId}?limit=${limit}&page=${page}`
      );
      const data = await response.json();

      if (data.success && data.data) {
        // Extract product IDs and add to wishlist set
        if (data.data.products) {
          /** @type {any[]} */
          const products = data.data.products;
          products.forEach((product) => {
            const productId = this.extractProductId(product.id);
            if (productId) {
              this.wishlistProducts.add(productId);
            }
          });
          this.saveWishlistToStorage();
        }
        return data.data;
      }
      return null;
    } catch (error) {
      console.error('Error loading wishlist products:', error);
      return null;
    }
  }

  // Initialize wishlist state from API on page load
  async initializeWishlistState() {
    // Re-check login status to ensure customer ID is available
    this.checkLoginStatus();

    // Load initial wishlist count
    await this.loadWishlistCount();

    // Sync heart icon states from stored wishlist
    this.syncHeartIconStates();

    if (!this.isLoggedIn || !this.customerId) return;

    // If on wishlist page, load products to sync state
    if (window.location.pathname.includes('/wishlist') || window.location.pathname.includes('/pages/wishlist')) {
      const data = await this.loadWishlistProducts(this.customerId, 1, 12);
      if (data && data.products) {
        // Update all heart icons on the page
        data.products.forEach((/** @type {any} */ product) => {
          const productId = this.extractProductId(product.id);
          if (productId) {
            this.updateWishlistState(productId, true);
          }
        });
      }
    }
  }

  // Sync heart icon states from stored wishlist
  syncHeartIconStates() {
    const heartButtons = document.querySelectorAll('[data-wishlist-heart]');
    heartButtons.forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      const productId = button.dataset.productId ? this.extractProductId(button.dataset.productId) : null;
      if (productId && this.wishlistProducts.has(productId)) {
        button.classList.add('wishlist-heart--active');
        button.setAttribute('aria-label', 'Remove from wishlist');
      } else {
        button.classList.remove('wishlist-heart--active');
        button.setAttribute('aria-label', 'Add to wishlist');
      }
    });
  }
}

// Initialize wishlist manager when DOM is ready
(function initWishlist() {
  const win = /** @type {any} */ (window);
  if (win.WishlistManager) {
    return;
  }

  function startWishlist() {
    try {
      const wishlistManager = new WishlistManager();
      win.WishlistManager = wishlistManager;
    } catch (error) {
      console.error('Wishlist: Initialization error', error);
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startWishlist);
  } else {
    // DOM already loaded, but wait a bit for other scripts
    setTimeout(startWishlist, 100);
  }
})();
